// Printing: the paper always gets the light theme and every image, whatever the visitor has scrolled to.
// The layout itself is CSS (scss/layout/components/_print.scss). Works for the print button and for Ctrl+P alike.

const IMAGE_WAIT = 4000; // ms the print dialog waits for images at most

let previousTheme = null;

const root = document.documentElement;

// dark mode is a data attribute (not a media query), so it has to be switched off for the sheet by hand
const useLightTheme = () => {
    if (null === previousTheme) {
        previousTheme = root.getAttribute('data-bs-theme') || '';
    }
    root.setAttribute('data-bs-theme', 'light');
};

const restoreTheme = () => {
    if (null === previousTheme) {
        return;
    }
    if ('' === previousTheme) {
        root.removeAttribute('data-bs-theme');
    } else {
        root.setAttribute('data-bs-theme', previousTheme);
    }
    previousTheme = null;
};

// images that are still waiting for their scroll position (native loading="lazy", see media/image.html.twig)
const loadImages = () => {
    document.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
    });

    const pending = Array.from(document.images).filter((image) => !image.complete);
    if (!pending.length) {
        return Promise.resolve();
    }

    const loaded = Promise.all(pending.map((image) => new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
    })));

    return Promise.race([loaded, new Promise((resolve) => window.setTimeout(resolve, IMAGE_WAIT))]);
};

// The source line on the foot of every sheet, in the bottom margin of the page (page margin boxes: Chrome and Edge).
// Elsewhere the line stays where it is, at the end of the text (CSS).
let footerStyle = null;

const addFooter = () => {
    const source = document.querySelector('.print-source');
    if (!source || !window.CSSMarginRule || footerStyle) {
        return;
    }

    // goes into a CSS string: no quotes or backslashes
    const text = source.textContent.trim().replace(/[\\"]/g, '').replace(/\s+/g, ' ');
    const box = 'font: 8pt "Open Sans", Arial, sans-serif; color: #555; border-top: 1px solid #999; padding-top: 2mm; vertical-align: top;';
    footerStyle = document.createElement('style');
    footerStyle.textContent = `@page {
        @bottom-left { content: "${text}"; width: 85%; ${box} }
        @bottom-right { content: counter(page) " / " counter(pages); text-align: right; ${box} }
    }`;
    document.head.appendChild(footerStyle);
    root.classList.add('print-margin-footer');
};

const removeFooter = () => {
    if (footerStyle) {
        footerStyle.remove();
        footerStyle = null;
    }
    root.classList.remove('print-margin-footer');
};

export function printPage() {
    useLightTheme();
    addFooter();

    return loadImages().then(() => window.print());
}

export default function initPrint() {
    window.addEventListener('beforeprint', () => {
        useLightTheme();
        addFooter();
        loadImages();
    });
    window.addEventListener('afterprint', () => {
        restoreTheme();
        removeFooter();
    });
}
